import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOutsideClick } from '@/common/hooks';
import { contactFieldKeyFromValue } from '../utils/dynamicContactVars';

export function VariableValueInput({
  id,
  value,
  placeholder,
  options,
  onChange,
  onPickField,
  disabled = false,
  inputClassName = '',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const [box, setBox] = useState(null);
  useOutsideClick([rootRef, menuRef], () => setOpen(false), open);

  useEffect(() => {
    if (!open || !rootRef.current) return undefined;
    const place = () => {
      const rect = rootRef.current.getBoundingClientRect();
      setBox({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const selectedKey = contactFieldKeyFromValue(value);

  return (
    <div ref={rootRef} className="relative w-full">
      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full h-11 pl-3.5 pr-9 rounded-xl bg-white border border-border-default text-main-black text-sm focus:outline-none focus:border-primary transition-all disabled:opacity-60 ${inputClassName}`}
      />
      {options.length > 0 && (
        <button
          type="button"
          aria-label="Choose contact field"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-primary disabled:opacity-60"
        >
          <svg
            className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
            />
          </svg>
        </button>
      )}
      {open &&
        box &&
        createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            style={{ top: box.top, left: box.left, width: box.width }}
            className="fixed z-[99999] bg-white border border-border-default rounded-xl py-1 shadow-md"
          >
            {options.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selectedKey === option.value}
                  onClick={() => {
                    onPickField(option.value);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-surface-mint ${
                    selectedKey === option.value
                      ? 'text-primary font-semibold'
                      : 'text-text-primary'
                  }`}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
